import React from 'react';
import ReactDOM from 'react-dom/client';
import { initSentry } from './config/sentry';
import './index.css';
import './styles/theme.css';
import App from './App';
import { registerServiceWorker, requestPersistentStorage, isPWA, isNativeApp } from './utils/pwa';
import { startNativeVersionGuard } from './utils/nativeVersionGuard';
import { initScrollDebug } from './utils/scrollDebug';

// Initialize Sentry before anything else
initSentry();

// Initialize theme from localStorage before first paint to avoid flash
// Applies to all pages including public (blog, landing) where useTheme() isn't mounted
(() => {
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark') {
      document.documentElement.setAttribute('data-theme', stored);
    } else {
      // No stored preference — default to dark (Helm is a dark-first app)
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  } catch {
    document.documentElement.setAttribute('data-theme', 'dark');
  }
})();

// Add native-app class when running in Capacitor (for safe area CSS)
if (isNativeApp()) {
  document.documentElement.classList.add('native-app');
  document.body.classList.add('native-app');
} else if (isPWA()) {
  // Add pwa-standalone class for installed PWA (same positioning strategy as native-app).
  // Uses a class instead of @media (display-mode: standalone) because CRA loads component
  // CSS after global CSS (index.css), silently overriding media-query rules
  // with equal specificity. A body-prefixed class always wins.
  document.documentElement.classList.add('pwa-standalone');
  document.body.classList.add('pwa-standalone');
}

initScrollDebug();

// Strip noisy console output in production (keep console.error for real issues)
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Register PWA service worker (web only — skipped on native)
registerServiceWorker();

// Native apps skip the SW, so they have no auto-update — guard against a stale WebView
// cache stranding users on an old build (bug-registry 2026-06-27). No-op on web.
startNativeVersionGuard();

// Request persistent storage for PWA mode
if (isPWA()) {
  requestPersistentStorage();
}

// Hide splash screen after React mount on native apps (remote URL mode).
// Use the Capacitor bridge directly — the @capacitor/splash-screen npm package
// is only in mobile/package.json, not web/package.json, so dynamic import() 404s.
if (isNativeApp()) {
  const cap = (window as any).Capacitor;
  if (cap?.Plugins?.SplashScreen?.hide) {
    cap.Plugins.SplashScreen.hide();
  } else if (cap?.registerPlugin) {
    try { cap.registerPlugin('SplashScreen').hide(); } catch { /* plugin not available */ }
  }
}
